import { CommonModule } from '@angular/common';
import { Component, Input, OnChanges, SimpleChanges } from '@angular/core';
import { CloudinaryModule } from '@cloudinary/ng';
import { Cloudinary, CloudinaryImage } from '@cloudinary/url-gen';
import { fill } from '@cloudinary/url-gen/actions/resize';
import { max } from '@cloudinary/url-gen/actions/roundCorners';
import { environment } from 'src/environments/environment';

/**
 * Compatibility wrapper for old cl-image selector
 * Provides backward compatibility with Cloudinary Angular 5.x API
 * while using the new @cloudinary/ng package
 */
@Component({
    selector: 'cl-image',
    template: '<advanced-image [cldImg]="img" [ngClass]="className" [ngStyle]="style"></advanced-image>',
    standalone: true,
    imports: [CommonModule, CloudinaryModule],
})
export class ClImageCompatComponent implements OnChanges {
    @Input('public-id') publicId: string = '';
    @Input() width: string | number = '';
    @Input() height: string | number = '';
    @Input() crop: string = '';
    @Input() radius: string = '';
    @Input() background: string = '';
    @Input() class: string = '';

    img: CloudinaryImage;
    className: string = '';
    style: any = {};

    private cld: Cloudinary;

    constructor() {
        // Initialize Cloudinary with cloud name from environment
        this.cld = new Cloudinary({
            cloud: {
                cloudName: environment.cloudinary?.cloud_name || 'demo',
            },
        });
    }

    ngOnChanges(changes: SimpleChanges): void {
        if (
            changes['publicId'] ||
            changes['width'] ||
            changes['height'] ||
            changes['crop'] ||
            changes['radius'] ||
            changes['background']
        ) {
            this.updateImage();
        }
        if (changes['class']) {
            this.className = this.class;
        }
    }

    private updateImage(): void {
        if (!this.publicId) {
            return;
        }

        // Create base image
        this.img = this.cld.image(this.publicId);

        // Apply transformations
        const transformations: any[] = [];

        // Handle dimensions and crop
        if (this.width || this.height) {
            const w = this.parseSize(this.width);
            const h = this.parseSize(this.height);

            if (this.crop === 'fill') {
                if (w && h) {
                    this.img.resize(fill().width(w).height(h));
                }
            } else {
                // For non-fill crops or auto, use style for dimensions
                if (w) this.style.width = typeof this.width === 'number' ? `${this.width}px` : this.width;
                if (h) this.style.height = typeof this.height === 'number' ? `${this.height}px` : this.height;
            }
        }

        // Handle radius
        if (this.radius) {
            if (this.radius === 'max') {
                this.img.roundCorners(max());
            }
            // Add other radius transformations as needed
        }

        // Handle background color
        if (this.background) {
            this.style.backgroundColor = this.background;
        }
    }

    private parseSize(size: string | number): number | undefined {
        if (typeof size === 'number') return size;
        if (!size) return undefined;

        // Remove 'px' or '%' and parse
        const match = String(size).match(/^(\d+)/);
        return match ? parseInt(match[1]) : undefined;
    }
}
